/**
 * Modal para crear/editar Asociación
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { X, Loader2 } from "lucide-react";
import {
  useCreateAssociation,
  useUpdateAssociation,
} from "@/hooks/useOrganization";
import type { Association } from "@/types";

const associationSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  code: z.string().min(2, "El código debe tener al menos 2 caracteres"),
  region: z.string().optional(),
  contactEmail: z.string().email("Email inválido").optional().or(z.literal("")),
  contactPhone: z.string().optional(),
});

type AssociationForm = z.infer<typeof associationSchema>;

interface AssociationModalProps {
  isOpen: boolean;
  onClose: () => void;
  association?: Association | null;
  unionId: string;
}

export default function AssociationModal({
  isOpen,
  onClose,
  association,
  unionId,
}: AssociationModalProps) {
  const isEditing = !!association;

  const createMutation = useCreateAssociation();
  const updateMutation = useUpdateAssociation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AssociationForm>({
    resolver: zodResolver(associationSchema),
    defaultValues: {
      name: association?.name || "",
      code: association?.code || "",
      region: association?.region || "",
      contactEmail: association?.contactEmail || "",
      contactPhone: association?.contactPhone || "",
    },
  });

  const onSubmit = async (data: AssociationForm) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: association.id, data });
      } else {
        await createMutation.mutateAsync({
          ...data,
          unionId,
          status: "active",
        } as any);
      }
      onClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Asociación" : "Nueva Asociación"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Nombre *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Asociación del Norte"
          />

          <Input
            label="Código *"
            {...register("code")}
            error={errors.code?.message}
            placeholder="Ej: AN"
          />

          <Input
            label="Región"
            {...register("region")}
            error={errors.region?.message}
            placeholder="Ej: Norte de Colombia"
          />

          <Input
            label="Email de contacto"
            type="email"
            {...register("contactEmail")}
            error={errors.contactEmail?.message}
            placeholder="contacto@asociacion.org"
          />

          <Input
            label="Teléfono de contacto"
            {...register("contactPhone")}
            error={errors.contactPhone?.message}
            placeholder="+57 300 123 4567"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
